// Reversos de tarjeta (Chile) — F1a. Búsqueda de candidatos para el tab "Ajuste de tarjeta" de
// VincularMovimientoModal, según reversos-webpay-diseno-2026-09-20.md §3.2/§3.3/§3.4.
//
// Separación deliberada entre lógica PURA (testeable sin red) y el fetcher que arma sus insumos con
// supabase-js — mismo patrón que scripts/dualrun-pago.mjs usa para el ledger de proveedor.

export const VENTANA_DIAS_CANDIDATOS = 90;

export function restarDias(fechaISO, dias) {
  const d = new Date(`${fechaISO}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().slice(0, 10);
}

// §3.2/§3.3/§3.4 — puro: recibe los candidatos ya traídos de la DB y decide qué mostrar.
// ajuste: { id, monto, tipo } (tipo de movimientos_bancarios: 'ingreso'|'egreso').
// candidatosAjuste: filas de movimientos_caja categoria_pago='ajuste_tarjeta' ya resueltas contra su
//   propio movimiento bancario ({ id, monto, descripcion, cuenta_er, fecha }) — pasar [] si
//   ajuste.tipo !== 'egreso' (§3.3 solo corre la pasada 1 para el patrón "Anulación").
// candidatosCompra: filas de movimientos_bancarios tipo='egreso' candidatas, con `movimiento_caja`
//   embebido (null si la compra sigue `pendiente`, sin fila de caja — §3.4).
export function resolverCandidatos({ ajuste, candidatosAjuste, candidatosCompra }) {
  const montoAbs = Math.abs(Number(ajuste.monto));

  const ajustesPrevios = (candidatosAjuste || []).filter(
    (c) => c.id !== ajuste.id && Math.abs(Number(c.monto)) === montoAbs
  );
  if (ajustesPrevios.length > 0) {
    // §3.3 pasada 1: ya hay un ajuste_tarjeta procesado con el mismo monto — el padre es ESE ajuste,
    // no la compra (cadena de 2 saltos hasta la compra real, ver §2 del diseño). `movimientoPadreId`
    // se expone para que `marcarNoElegibles` pueda caminar la cadena hasta la compra real (hallazgo
    // reversos-webpay-f1b-diseno-2026-09-29.md §B.4) — `elegibleF1a: true` es solo el DEFAULT (un
    // ajuste huérfano o cuya cadena no resuelve a una compra con documento sigue elegible); el guard
    // lo sobreescribe si la cadena termina en una compra con MBF/libro real.
    return {
      tipo: "ajuste_previo",
      candidatos: ajustesPrevios.map((c) => ({
        movimientoCajaId: c.id,
        movimientoPadreId: c.movimiento_padre_id,
        origen: "ajuste_tarjeta",
        fecha: c.fecha,
        descripcion: c.descripcion,
        cuentaEr: c.cuenta_er,
        monto: c.monto,
        elegibleF1a: true,
      })),
      compraPendiente: [],
    };
  }

  // §3.2 pasada 2 (o directa, para el caso normal de reverso-ingreso).
  const conCaja = (candidatosCompra || []).filter(
    (cb) => cb.movimiento_caja && cb.movimiento_caja.categoria_pago !== "ajuste_tarjeta"
  );
  const sinCaja = (candidatosCompra || []).filter((cb) => !cb.movimiento_caja);

  return {
    tipo: "compra",
    candidatos: conCaja.map((cb) => ({
      movimientoCajaId: cb.movimiento_caja.id,
      movimientoBancarioId: cb.id,
      origen: "compra",
      fecha: cb.fecha,
      descripcion: cb.descripcion_raw,
      categoriaPago: cb.movimiento_caja.categoria_pago,
      cuentaEr: cb.movimiento_caja.cuenta_er,
      entidadTipo: cb.movimiento_caja.entidad_tipo,
      entidadId: cb.movimiento_caja.entidad_id,
      entidadNombre: cb.movimiento_caja.entidad_nombre,
      monto: cb.movimiento_caja.monto,
    })), // orden ya viene de la query (fecha DESC)
    compraPendiente: sinCaja.map((cb) => ({
      movimientoBancarioId: cb.id,
      fecha: cb.fecha,
      descripcion: cb.descripcion_raw,
    })),
  };
}

// Tope duro de saltos al caminar una cadena de `movimiento_padre_id` entre ajustes de tarjeta
// (hallazgo reversos-webpay-f1b-diseno-2026-09-29.md §B.4). En el universo real de 26 movimientos la
// cadena más larga tiene 2 saltos (Anulación → Reverso → compra); 5 deja margen amplio sin abrir la
// puerta a un loop sin fin ante una cadena corrupta o circular (10.177 — cortar con error, no colgar).
export const MAX_SALTOS_CADENA_AJUSTE = 5;

// Pura — camina `movimiento_padre_id` desde un candidato "ajuste_previo" (§3.3 pasada 1, un Reverso o
// Anulación YA procesado) hasta encontrar la fila de la COMPRA real (categoria_pago distinto de
// 'ajuste_tarjeta'): un ajuste nunca tiene su propio MBF, solo la compra que originó la cadena lo
// tiene, así que el guard de alcance debe evaluar el documento fiscal ahí, no en el escalón
// intermedio. `cajaPorId` ya viene armado por el fetcher (BFS acotado, ver más abajo).
// Devuelve: {estado:'resuelta', movimientoBancarioId, saltos} | {estado:'huerfano'} (la cadena termina
// en un ajuste sin padre — nunca hubo compra) | {estado:'incompleta'|'truncada', motivo} (10.177: la
// cadena no se pudo resolver del todo — nunca se trata como "sin documento" en silencio).
export function resolverCompraDesdeCadena(movimientoPadreId, cajaPorId, { maxSaltos = MAX_SALTOS_CADENA_AJUSTE } = {}) {
  let actualId = movimientoPadreId;
  let saltos = 0;
  while (actualId != null) {
    if (saltos >= maxSaltos) {
      return {
        estado: "truncada",
        motivo: `la cadena de ajustes supera ${maxSaltos} saltos — posible ciclo, se corta con error en vez de colgar (10.177)`,
      };
    }
    const fila = cajaPorId.get(actualId);
    if (!fila) {
      return { estado: "incompleta", motivo: `la fila ${actualId} de la cadena no está en los datos fetcheados` };
    }
    if (fila.categoria_pago !== "ajuste_tarjeta") {
      return { estado: "resuelta", movimientoBancarioId: fila.movimiento_bancario_id, saltos };
    }
    actualId = fila.movimiento_padre_id;
    saltos += 1;
  }
  return { estado: "huerfano" };
}

// Guard de alcance (contrapartida-2026-09-27.md §C/§E): un candidato "compra" con documento fiscal
// real detrás (MBF o evento de libro sobre su propio movimiento_bancario_id) es F1b, no F1a — se
// marca, nunca se descarta en silencio (10.177). Extendido (§B.4 del diseño F1b) para que un
// candidato "ajuste_previo" (origen de la pasada 1, §3.3) evalúe el documento fiscal de la compra
// REAL al final de su cadena de `movimiento_padre_id`, no en el ajuste intermedio (que nunca tiene
// MBF propio) — sin esto, una Anulación que encuentra un Reverso ya procesado de Google/AccuWeather
// quedaría "elegible F1a" por error, inconsistente con el Reverso que está deshaciendo.
// mbfPorMovimiento / libroPorMovimiento: Map<movimiento_bancario_id, boolean>.
// cajaPorId: Map<id, {id, categoria_pago, movimiento_bancario_id, movimiento_padre_id}> — opcional,
// solo hace falta cuando hay candidatos "ajuste_previo" con `movimientoPadreId`.
export function marcarNoElegibles(candidatos, { mbfPorMovimiento, libroPorMovimiento, cajaPorId = new Map() }) {
  return candidatos.map((c) => {
    if (c.origen === "ajuste_tarjeta") {
      if (c.movimientoPadreId == null) return { ...c, elegibleF1a: true }; // ajuste huérfano — nunca hubo compra.
      const resuelta = resolverCompraDesdeCadena(c.movimientoPadreId, cajaPorId);
      if (resuelta.estado === "huerfano") return { ...c, elegibleF1a: true };
      if (resuelta.estado === "resuelta") {
        const tieneDocumento =
          !!mbfPorMovimiento.get(resuelta.movimientoBancarioId) ||
          !!libroPorMovimiento.get(resuelta.movimientoBancarioId);
        if (tieneDocumento) {
          return {
            ...c,
            elegibleF1a: false,
            motivoNoElegible: `la cadena de ajustes (${resuelta.saltos + 1} salto(s)) termina en una compra con documento fiscal real (MBF/libro) — requiere F1b, no F1a`,
          };
        }
        return { ...c, elegibleF1a: true };
      }
      // 'incompleta' / 'truncada': nunca se trata como "sin documento" en silencio (10.177) — se
      // bloquea con el motivo explícito, el lado conservador ante un dato que no se pudo verificar.
      return {
        ...c,
        elegibleF1a: false,
        motivoNoElegible: `no se pudo resolver la cadena de ajustes: ${resuelta.motivo}`,
      };
    }
    if (c.origen !== "compra") return { ...c, elegibleF1a: true };
    const tieneDocumento =
      !!mbfPorMovimiento.get(c.movimientoBancarioId) || !!libroPorMovimiento.get(c.movimientoBancarioId);
    if (tieneDocumento) {
      return {
        ...c,
        elegibleF1a: false,
        motivoNoElegible: "la compra tiene documento fiscal real (MBF/libro) detrás — requiere F1b, no F1a",
      };
    }
    return { ...c, elegibleF1a: true };
  });
}

// Auxiliar del fetcher — arma el `cajaPorId` que necesita `resolverCompraDesdeCadena`, caminando
// `movimiento_padre_id` hacia arriba en bloques (BFS por `id IN (...)`), hasta el mismo tope duro de
// saltos (10.177 — nunca un loop sin fin ante una cadena corrupta/circular; una cadena en ciclo deja
// de agregar ids nuevos a `pendientes` apenas revisita una fila ya resuelta, así que termina sola
// incluso antes del tope).
async function resolverCajaPorIdParaCadenas(client, idsIniciales, { maxSaltos = MAX_SALTOS_CADENA_AJUSTE } = {}) {
  const cajaPorId = new Map();
  let pendientes = [...new Set(idsIniciales)];
  let saltos = 0;
  while (pendientes.length > 0 && saltos < maxSaltos) {
    const { data, error } = await client
      .from("movimientos_caja")
      .select("id, categoria_pago, movimiento_bancario_id, movimiento_padre_id")
      .in("id", pendientes);
    if (error) throw error; // 10.170 — nunca tragar error a [].
    const siguientes = [];
    for (const fila of data || []) {
      cajaPorId.set(fila.id, fila);
      if (
        fila.categoria_pago === "ajuste_tarjeta" &&
        fila.movimiento_padre_id != null &&
        !cajaPorId.has(fila.movimiento_padre_id)
      ) {
        siguientes.push(fila.movimiento_padre_id);
      }
    }
    pendientes = [...new Set(siguientes)];
    saltos += 1;
  }
  return cajaPorId;
}

// Fetcher — arma los insumos de resolverCandidatos()/marcarNoElegibles() con supabase-js. Usado por
// el tab "Ajuste de tarjeta" (Commit 2, F1a) — Commit 1 solo lo mide, no lo cablea a UI.
export async function buscarCandidatosAjusteTarjeta(client, ajuste) {
  const { id, monto, tipo, cuenta_bancaria_id: cuentaBancariaId, pais, fecha } = ajuste;
  const fechaMin = restarDias(fecha, VENTANA_DIAS_CANDIDATOS);

  // §3.3: la pasada 1 (ajustes ya procesados) solo corre para el patrón "Anulación" (egreso).
  let ajustePrevioQuery = Promise.resolve({ data: [], error: null });
  if (tipo === "egreso") {
    ajustePrevioQuery = client
      .from("movimientos_caja")
      .select(
        "id, monto, descripcion, cuenta_er, movimiento_padre_id, movimientos_bancarios!inner(fecha,cuenta_bancaria_id,pais)"
      )
      .eq("categoria_pago", "ajuste_tarjeta")
      .eq("movimientos_bancarios.cuenta_bancaria_id", cuentaBancariaId)
      .eq("movimientos_bancarios.pais", pais)
      .lte("movimientos_bancarios.fecha", fecha)
      .gte("movimientos_bancarios.fecha", fechaMin);
  }

  const [ajusteRes, compraRes] = await Promise.all([
    ajustePrevioQuery,
    client
      .from("movimientos_bancarios")
      .select(
        "id, fecha, descripcion_raw, movimiento_caja:movimientos_caja(id,categoria_pago,cuenta_er,entidad_tipo,entidad_id,entidad_nombre,monto)"
      )
      .eq("tipo", "egreso")
      .eq("monto", monto)
      .eq("cuenta_bancaria_id", cuentaBancariaId)
      .eq("pais", pais)
      .neq("id", id)
      .not("descripcion_raw", "ilike", "%reverso%")
      .not("descripcion_raw", "ilike", "%anulaci%")
      .lte("fecha", fecha)
      .gte("fecha", fechaMin)
      .order("fecha", { ascending: false }),
  ]);
  if (ajusteRes.error) throw ajusteRes.error; // 10.170 — nunca tragar error a [].
  if (compraRes.error) throw compraRes.error;

  const candidatosAjuste = (ajusteRes.data || []).map((c) => ({
    id: c.id,
    monto: c.monto,
    descripcion: c.descripcion,
    cuenta_er: c.cuenta_er,
    movimiento_padre_id: c.movimiento_padre_id,
    fecha: c.movimientos_bancarios?.fecha,
  }));
  const candidatosCompra = (compraRes.data || []).map((cb) => ({
    id: cb.id,
    fecha: cb.fecha,
    descripcion_raw: cb.descripcion_raw,
    movimiento_caja: Array.isArray(cb.movimiento_caja) ? cb.movimiento_caja[0] || null : cb.movimiento_caja,
  }));

  const resultado = resolverCandidatos({ ajuste, candidatosAjuste, candidatosCompra });
  if (resultado.candidatos.length === 0) return resultado;

  // Guard de alcance (F1a vs F1b) — corre para los dos tipos de resultado ("compra" y "ajuste_previo";
  // hallazgo reversos-webpay-f1b-diseno-2026-09-29.md §B.4: antes solo corría para "compra", dejando
  // pasar sin chequear la cadena de un ajuste_previo ya procesado).
  const idsCandidatosCompra = resultado.candidatos
    .filter((c) => c.origen === "compra")
    .map((c) => c.movimientoBancarioId);
  const candidatosAjustePrevio = resultado.candidatos.filter((c) => c.origen === "ajuste_tarjeta");

  let cajaPorId = new Map();
  if (candidatosAjustePrevio.length > 0) {
    const padresIniciales = candidatosAjustePrevio.map((c) => c.movimientoPadreId).filter((x) => x != null);
    if (padresIniciales.length > 0) cajaPorId = await resolverCajaPorIdParaCadenas(client, padresIniciales);
  }
  const idsResueltosPorCadena = candidatosAjustePrevio
    .map((c) => (c.movimientoPadreId != null ? resolverCompraDesdeCadena(c.movimientoPadreId, cajaPorId) : null))
    .filter((r) => r && r.estado === "resuelta")
    .map((r) => r.movimientoBancarioId);

  const movimientoBancarioIds = [...new Set([...idsCandidatosCompra, ...idsResueltosPorCadena])];

  let mbfPorMovimiento = new Map();
  let libroPorMovimiento = new Map();
  if (movimientoBancarioIds.length > 0) {
    const [{ data: mbfRows, error: errMbf }, { data: libroRows, error: errLibro }] = await Promise.all([
      client
        .from("movimiento_bancario_facturas")
        .select("movimiento_bancario_id")
        .in("movimiento_bancario_id", movimientoBancarioIds),
      client
        .from("aplicaciones_saldo")
        .select("origen_id")
        .eq("origen_tipo", "movimiento_bancario")
        .in("origen_id", movimientoBancarioIds),
    ]);
    if (errMbf) throw errMbf;
    if (errLibro) throw errLibro;
    mbfPorMovimiento = new Map((mbfRows || []).map((r) => [r.movimiento_bancario_id, true]));
    libroPorMovimiento = new Map((libroRows || []).map((r) => [r.origen_id, true]));
  }

  return {
    ...resultado,
    candidatos: marcarNoElegibles(resultado.candidatos, { mbfPorMovimiento, libroPorMovimiento, cajaPorId }),
  };
}
